import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types/swiper-options'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'

const createNavigation = (carousel: HTMLElement) => {
    const navigation: HTMLElement = document.createElement('nav')
    navigation.classList.add('swiper-navigation')
    carousel.appendChild(navigation)

    const buttonPrev: HTMLButtonElement = document.createElement('button')
    buttonPrev.classList.add('swiper-navigation__button')
    buttonPrev.classList.add('swiper-navigation__button-prev')
    buttonPrev.ariaLabel = `{{js.swiper.navigation.slide.previous.aria-label}}`

    const buttonNext: HTMLButtonElement = document.createElement('button')
    buttonNext.classList.add('swiper-navigation__button')
    buttonNext.classList.add('swiper-navigation__button-next')
    buttonNext.ariaLabel = `{{js.swiper.navigation.slide.next.aria-label}}`

    navigation.appendChild(buttonPrev)
    navigation.appendChild(buttonNext)

    carousel.appendChild(navigation)
}

const createPagination = (carousel: HTMLElement) => {
    const pagination: HTMLElement = document.createElement('nav')
    pagination.classList.add('swiper-pagination')
    carousel.appendChild(pagination)
}

const createCallToAction = (swiper: Swiper) => {
    const container = document.createElement('div')
    container.className = 'swiper-cta'

    const buttons = swiper.el.querySelectorAll<HTMLAnchorElement>('.swiper-slide.attrax-button')
    buttons.forEach((button) => {
        button.remove()
        button.classList.remove('swiper-slide')
        button.removeAttribute('style')
        button.removeAttribute('tabindex')
        button.href = button.href.toLowerCase()
        container.appendChild(button)
    })

    swiper.update()
    swiper.el.appendChild(container)
}

const createWrapper = (carousel: HTMLElement) => {
    const swiperWrapper = carousel.querySelector<HTMLDivElement>('.swiper-wrapper')
    if (!swiperWrapper) return
    const wrapper = document.createElement('div')
    wrapper.classList.add('swiper-wrapper-container')
    wrapper.appendChild(swiperWrapper)
    carousel.appendChild(wrapper)
}

const createCarousel = (query: string, swiperOptions: SwiperOptions) => {
    const carousels = document.querySelectorAll<HTMLElement>(query)
    carousels.forEach((carousel) => {
        carousel.classList.add('swiper')

        const children = [...carousel.children]
        children.forEach((child: HTMLElement) => {
            child.classList.add('swiper-slide')
        })

        if (swiperOptions.navigation !== false) {
            createNavigation(carousel)
            swiperOptions.navigation = {
                prevEl: '.swiper-navigation__button-prev',
                nextEl: '.swiper-navigation__button-next',
            }
        }

        if (swiperOptions.pagination !== false) {
            createPagination(carousel)
            swiperOptions.pagination = {
                el: '.swiper-pagination',
                clickable: true,
                bulletElement: 'button',
            }
        }

        const swiper = new Swiper(carousel, {
            modules: [Navigation, Pagination, Autoplay, EffectFade],
            createElements: true,
            watchOverflow: true,
            watchSlidesProgress: true,
            ...swiperOptions,
        })

        createWrapper(carousel)
    })
}

// Update createCarouselReact to return the swiper instance
const createCarouselReact = (ref: React.RefObject<HTMLDivElement>, swiperOptions: SwiperOptions) => {
    const carousel = ref.current
    if (!carousel) return null

    carousel.classList.add('swiper')

    const children = [...carousel.children]
    children.forEach((child: HTMLElement) => {
        child.classList.add('swiper-slide')
    })

    if (swiperOptions.navigation !== false) {
        createNavigation(carousel)
        swiperOptions.navigation = {
            prevEl: '.swiper-navigation__button-prev',
            nextEl: '.swiper-navigation__button-next',
        }
    }

    if (swiperOptions.pagination !== false) {
        createPagination(carousel)
        swiperOptions.pagination = {
            el: '.swiper-pagination',
            clickable: true,
            bulletElement: 'button',
        }
    }

    const swiper = new Swiper(carousel, {
        modules: [Navigation, Pagination, Autoplay],
        createElements: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        ...swiperOptions,
    })

    createWrapper(carousel)

    return swiper // Return the swiper instance
}


const createJobsCarousel = () => {
    const jobsCarousels = document.querySelectorAll<HTMLDivElement>('.jobs-carousel .attrax-list-widget__list--has-items');

    jobsCarousels.forEach((carouselElement) => {
        const slideCount = carouselElement.children.length;
        const refObject = { current: carouselElement };

        const swiperOptions = {
            slidesPerView: 1,
            spaceBetween: 30,
            breakpoints: {
                666: {
                    slidesPerView: Math.min(slideCount, 2),
                },
                991: {
                    slidesPerView: Math.min(slideCount, 3),
                },
            },
            on: {
                afterInit: (swiper) => {
                    createCallToAction(swiper);
                }
            }
        };

        createCarouselReact(refObject, swiperOptions);
    });
};

const createPostsCarousel = () => {
    createCarousel('.post-carousel .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 25,
        centeredSlides: true,
        loop: true,
        breakpoints: {
            666: {
                slidesPerView: 1.5,
            },
        },
        on: {
            afterInit: (swiper) => {
                createCallToAction(swiper)
            }
        }
    })
}

const createScrollingCtaCarousel = () => {
    createCarousel('.corp-scrolling-cta-banner', {
        navigation: false,
        pagination: false,
        loop: true,
        grabCursor: true,
        autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
        },
    })
}

const createQuotesCarousel = () => {
    createCarousel('.corp-quotes-banner', {
        navigation: false,
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 30,
        autoplay: {
            delay: 3000,
            pauseOnMouseEnter: true,
        },
        breakpoints: {
            666: {
                slidesPerView: 2,
            },
            991: {
                slidesPerView: 3,
            },
        },
    })
}

const createAwardsCarousel = () => {
    createCarousel('.corp-awards-banner', {
        pagination: true,
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 60,
        autoplay: {
            delay: 3000,
            pauseOnMouseEnter: true,
        },
        breakpoints: {
            666: {
                slidesPerView: 4,
                spaceBetween: 30,
            },
            991: {
                slidesPerView: 5,
                spaceBetween: 30,
            },
        },
    })
}

export const createTimelineCarousel = (
    containerRef: React.RefObject<HTMLDivElement | null>,
    onSlideChange?: (activeIndex: number) => void
) => {
    if (!containerRef.current) return;

    // Find the carousel element within the container
    const carouselElement = containerRef.current.querySelector('.snippet-timeline__content-container') as HTMLElement;
    if (!carouselElement) return;

    // Create a ref-like object for the carousel element
    const carouselRef = { current: carouselElement as HTMLDivElement };

    return createCarouselReact(carouselRef, {
        pagination: true,
        loop: true,
        grabCursor: true,
        slidesPerView: 3,
        centeredSlides: true,
        spaceBetween: 30,
        breakpoints: {
            800: {
                slidesPerView: 5,
                spaceBetween: 30
            },
        },
        on: {
            slideChange: function () {
                if (onSlideChange) onSlideChange(this.realIndex);
            },
            afterInit: function (swiper) {
                const nav = swiper.el.querySelector('.swiper-navigation');
                const container = swiper.el.closest('.snippet-timeline__container');
                if (nav && container) {
                    container.appendChild(nav);
                }
            }
        }
    });
}


export const createApplicationProcessCarousel = (
    containerRef: React.RefObject<HTMLDivElement | null>,
    onSlideChange?: (activeIndex: number) => void
) => {
    if (!containerRef.current) return;


    const carouselElement = containerRef.current.querySelector('.snippet-application-process__content-container') as HTMLElement;
    if (!carouselElement) return;


    const carouselRef = { current: carouselElement as HTMLDivElement };

    return createCarouselReact(carouselRef, {
        pagination: true,
        loop: true,
        grabCursor: true,
        slidesPerView: 3,
        centeredSlides: true,
        spaceBetween: 30,
        breakpoints: {
            800: {
                slidesPerView: 5,
                spaceBetween: 30
            },
        },
        on: {
            slideChange: function () {
                if (onSlideChange) onSlideChange(this.realIndex);
            },
            afterInit: function (swiper) {
                const nav = swiper.el.querySelector('.swiper-navigation');
                const container = swiper.el.closest('.snippet-application-process__container');
                if (nav && container) {
                    container.appendChild(nav);
                }
            }
        }
    });
}


const createFeaturedCarousel = () => {
    createCarousel('.banner-featured-carousel', {
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 60,
        pagination: true,
    })
}

const createImageCarousel = () => {
    createCarousel('.banner-image-carousel', {
        pagination: true,
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 60,
        breakpoints: {
            666: {
                slidesPerView: 3,
                spaceBetween: 30,
            },
            991: {
                slidesPerView: 5,
                spaceBetween: 30,
            },
        },
    })
}

const createHeroCarousel = () => {
    const isMobile = window.innerWidth <= 991;

    createCarousel(isMobile ? 'banner-home-hero-mobile__wrapper' : '.corp-home-page-hero', {
        autoplay: {
            delay: 2500,
            disableOnInteraction: true,
        },
        speed: 1000,
        slidesPerView: 1,
        navigation: false,
        pagination: false,
        loop: true,
    })
}



export const initialise = () => {
    createJobsCarousel()
    createPostsCarousel()
    createScrollingCtaCarousel()
    createQuotesCarousel()
    createAwardsCarousel()
    createFeaturedCarousel()
    createImageCarousel()
    createHeroCarousel()
}